import { ReactNode } from "react";
import UILoader from "./UILoader";
import { makeStyles } from "@mui/styles";
import { MUICard, MUICardProps, UICardActions, UICardContent, UICardHeader } from "../index";
import UIText from "./UITypography";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";

export interface UICardProps extends MUICardProps {
  children: ReactNode;
  headerTitle?: ReactNode;
  headerProps?: any;
  light?: boolean;
  fetchingText?: string;
  isFetching?: boolean;
  isSuccess?: boolean;
  height?: string;
  padding?: string;
  style?: any;
  contentStyle?: any;
  errorMessage?: ReactNode;
  titleIndented?: boolean;
  className?: string;
  hasBorder?: boolean;
  elevation?: number;
  hasHover?: boolean;
  actions?: ReactNode[];
  isSimpleCard?: boolean;
  innerRef?: any;
}

const UICard = ({
  children,
  headerTitle,
  headerProps = {},
  light = true,
  fetchingText,
  isFetching = false,
  isSuccess = true,
  height = "100%",
  padding = "12px",
  style = {},
  contentStyle = {},
  errorMessage,
  className = "",
  hasBorder = false,
  elevation = 1,
  hasHover = false,
  actions = [],
  isSimpleCard = false,
  innerRef,
  ref,
  ...props
}: UICardProps) => {
  const classes = useStyles({ light: light });

  if (isSimpleCard) {
    return (
      <MUICard elevation={elevation} className={className}>
        {children}
      </MUICard>
    );
  }
  return (
    <MUICard
      className={[classes.root, hasBorder && classes.bordered, hasHover && classes.hover, className]
        .filter(Boolean)
        .join(" ")}
      style={{ padding: headerTitle ? "0" : padding, ...style }}
      elevation={elevation}
      data-cy="ui-card"
      ref={innerRef}
      {...props}
    >
      <>
        {Boolean(headerTitle) && (
          <UICardHeader
            title={headerTitle}
            className={classes.headerTitle}
            titleTypographyProps={{ align: "center" }}
            {...headerProps}
          />
        )}
        <UICardContent className={classes.content} style={{ height: height, padding: padding, ...contentStyle }}>
          {!isFetching && isSuccess && children}
          {!isFetching && !isSuccess && <div className={classes.progress}>{errorMessage}</div>}
          {isFetching && (
            <div className={classes.progress}>
              <UILoader />
              {Boolean(fetchingText) && <UIText>{fetchingText}</UIText>}
            </div>
          )}
        </UICardContent>
        {Boolean(actions.length) && <UICardActions>{actions}</UICardActions>}
      </>
    </MUICard>
  );
};

export default UICard;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      height: "100%",
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    bordered: {
      borderLeft: "3px solid",
      borderLeftColor: theme.palette.primary.main,
    },
    hover: {
      transition: "box-shadow .15s ease, transform .15s ease, border-color .15s ease",
      cursor: "pointer",
      "&:hover, &:focus-visible": {
        transform: "translateY(-2px)",
        boxShadow: theme.shadows[4],
        borderColor: theme.palette.primary.main,
      },
    },
    content: {
      display: "flex",
      justifyContent: "space-between",
      flexDirection: "column",
      "&:last-child": {
        paddingBottom: "0px",
      },
    },
    headerTitle: {
      padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
    },
    progress: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      height: "100%",
      "& p": {
        marginTop: "20px",
      },
    },
  })
);
